import { useTranslations } from '@vocab/react';
import { Box, Stack, Text } from 'braid-design-system';
import { useMemo, type ComponentProps } from 'react';

import commonTranslations from '../../.vocab';
import { GenAIChatLoadingOverlay } from '../../LoadingOverlay';
import { useCareerFeedAIAssistant } from '../../hooks/useCareerFeedAIAssistant';
import type { ErrorType } from '../../types';
import { CareerFeedAIAssistantDrawer } from '../CareerFeedAIAssistantDrawer/CareerFeedAIAssistantDrawer';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TextSection } from '../TextSection';

import * as styles from './CareerFeedAIAssistantContentFrame.css';

interface CareerFeedAIAssistantBaseContentProps {
  headMessage?:
    | {
        size?: ComponentProps<typeof TextSection>['size'];
        text: ComponentProps<typeof TextSection>['children'];
      }
    | string;
  contentSection?: ComponentProps<typeof Stack>['children'];
  ctaSection?: ComponentProps<typeof Stack>['children'];
  errorType?: ComponentProps<typeof ErrorMessage>['errorType'] | null;
  loading: boolean;
}

interface CareerFeedAIAssistantWidgetContentProps
  extends Omit<CareerFeedAIAssistantBaseContentProps, 'widgetCtaSection'> {
  socialProofSection?: ComponentProps<typeof Stack>['children'];
}

interface CareerFeedAIAssistantDrawerContentProps
  extends CareerFeedAIAssistantBaseContentProps {
  shouldOpenDrawer?: boolean;
  onDrawerClose?: () => void;
  progressBar: CareerFeedAIAssistantContentFrameProps['progressBar'];
}

interface CareerFeedAIAssistantContentFrameProps {
  widgetHeadMessage: CareerFeedAIAssistantBaseContentProps['headMessage'];
  widgetContentSection: CareerFeedAIAssistantWidgetContentProps['contentSection'];
  widgetCtaSection: CareerFeedAIAssistantWidgetContentProps['ctaSection'];
  drawerHeadMessage?: CareerFeedAIAssistantBaseContentProps['headMessage'];
  drawerContentSection?: CareerFeedAIAssistantDrawerContentProps['contentSection'];
  drawerCtaSection?: CareerFeedAIAssistantDrawerContentProps['ctaSection'];
  socialProofSection?: CareerFeedAIAssistantWidgetContentProps['socialProofSection'];
  shouldOpenDrawer?: CareerFeedAIAssistantDrawerContentProps['shouldOpenDrawer'];
  onDrawerClose?: CareerFeedAIAssistantDrawerContentProps['onDrawerClose'];
  errorType?: CareerFeedAIAssistantBaseContentProps['errorType'];
  loadingOnWidget: CareerFeedAIAssistantBaseContentProps['loading'];
  loadingOnDrawer: CareerFeedAIAssistantBaseContentProps['loading'];
  progressBar?: ComponentProps<typeof ProgressBar>;
}

const ErrorMessage = ({ errorType }: { errorType: ErrorType }) => {
  const { t } = useTranslations(commonTranslations);

  const errorText = useMemo(() => {
    if (errorType === 'INPUT_OR_OUTPUT_BLOCKED') {
      return t('I can only help you with your job search.');
    } else if (errorType === 'TOO_MANY_REQUESTS') {
      return t('You’ve done this too many times today. Try again later.');
    }

    return t('Can we try that again? We’re having trouble connecting.');
  }, [errorType, t]);

  return <Text tone="critical">{errorText}</Text>;
};

const CareerFeedAIAssistantWidgetContent = ({
  headMessage,
  errorType,
  contentSection,
  socialProofSection,
  loading,
}: CareerFeedAIAssistantWidgetContentProps) => (
  <GenAIChatLoadingOverlay isGenerating={loading}>
    <Box paddingY="xxsmall">
      <Stack space="medium">
        {typeof headMessage === 'string' ? (
          <TextSection>{headMessage}</TextSection>
        ) : (
          <TextSection size={headMessage?.size}>
            {headMessage?.text}
          </TextSection>
        )}
        {socialProofSection}
        {contentSection}
        {errorType ? <ErrorMessage errorType={errorType} /> : null}
      </Stack>
    </Box>
  </GenAIChatLoadingOverlay>
);

const CareerFeedAIAssistantDrawerContent = ({
  headMessage,
  errorType,
  contentSection,
  ctaSection,
  loading,
  shouldOpenDrawer = false,
  onDrawerClose,
  progressBar,
}: CareerFeedAIAssistantDrawerContentProps) => (
  <CareerFeedAIAssistantDrawer
    shouldOpen={shouldOpenDrawer}
    onClose={onDrawerClose}
  >
    <GenAIChatLoadingOverlay isGenerating={loading}>
      <Box
        className={styles.container}
        display="flex"
        flexGrow={1}
        paddingY="xxsmall"
      >
        <Stack space="medium">
          {progressBar && (
            <ProgressBar
              currentQuestion={progressBar.currentQuestion}
              totalQuestions={progressBar.totalQuestions}
            />
          )}
          {typeof headMessage === 'string' ? (
            <TextSection>{headMessage}</TextSection>
          ) : (
            <TextSection size={headMessage?.size}>
              {headMessage?.text}
            </TextSection>
          )}
          {contentSection}
          {errorType ? <ErrorMessage errorType={errorType} /> : null}
        </Stack>
      </Box>
      <Box display={{ mobile: 'none', tablet: 'block' }}>{ctaSection}</Box>
    </GenAIChatLoadingOverlay>
    <Box display={{ mobile: 'block', tablet: 'none' }} width="full">
      {ctaSection}
    </Box>
  </CareerFeedAIAssistantDrawer>
);

export const CareerFeedAIAssistantContentFrame = ({
  widgetHeadMessage,
  widgetContentSection,
  widgetCtaSection,
  drawerHeadMessage,
  drawerContentSection,
  drawerCtaSection,
  errorType,
  socialProofSection,
  onDrawerClose,
  loadingOnWidget = false,
  loadingOnDrawer = false,
  progressBar,
}: CareerFeedAIAssistantContentFrameProps) => {
  const { openDesktopDrawer } = useCareerFeedAIAssistant();

  return (
    <>
      <Stack space="small">
        <CareerFeedAIAssistantWidgetContent
          headMessage={widgetHeadMessage}
          errorType={errorType}
          contentSection={widgetContentSection}
          socialProofSection={socialProofSection}
          loading={loadingOnWidget}
        />
        <Stack space="medium">{widgetCtaSection}</Stack>
      </Stack>
      <CareerFeedAIAssistantDrawerContent
        headMessage={drawerHeadMessage}
        errorType={errorType}
        contentSection={drawerContentSection}
        ctaSection={drawerCtaSection}
        loading={loadingOnDrawer}
        shouldOpenDrawer={openDesktopDrawer}
        onDrawerClose={onDrawerClose}
        progressBar={progressBar}
      />
    </>
  );
};
